import { create } from 'zustand';

// Education structure
export interface TutorEducation {
  gpa?: number;
  field: string;
  degree: string;
  country: string;
  institution: string;
  year_of_completion: number;
  is_custom_degree: boolean;
  gpa_total?: number;
  level: string;
}

// Experience structure
export interface TutorExperience {
  role: "online" | "private" | "assessment" | "classroom" | "curriculum" | string;
  years: number;
  end_year?: number | null;
  location: string;
  start_year?: number | null;
  organization: string;
}

interface TutorOnboardingState {
  // Step management
  currentStep: number;
  setCurrentStep: (step: number) => void;
  
  // Step 1: Subject selection
  selectedSubjects: string[];
  setSelectedSubjects: (subjects: string[]) => void;
  
  // Step 2: Education (now JSONB array)
  education: TutorEducation[];
  setEducation: (education: TutorEducation[]) => void;
  
  // Step 3: Teaching experience (now JSONB array)
  experience: TutorExperience[];
  setExperience: (experience: TutorExperience[]) => void;
  
  // Step 4: Personal info
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  bio: string;
  setBio: (bio: string) => void;
  hourlyRate: string;
  setHourlyRate: (rate: string) => void;
  
  // CV file path
  cvFilePath: string;
  setCvFilePath: (path: string) => void;
  
  // CV file
  cvFile: File | null;
  setCvFile: (file: File | null) => void;
  
  // Navigation helpers
  nextStep: () => void;
  prevStep: () => void;
  isNextDisabled: () => boolean;
  
  // Reset store
  resetStore: () => void;
}

export const useTutorOnboardingStore = create<TutorOnboardingState>((set, get) => ({
  // Step management
  currentStep: 1,
  setCurrentStep: (step) => set({ currentStep: step }),
  
  // Step 1: Subject selection
  selectedSubjects: [],
  setSelectedSubjects: (subjects) => set({ selectedSubjects: subjects }),
  
  // Step 2: Education (now JSONB array)
  education: [],
  setEducation: (education) => set({ education: education }),
  
  // Step 3: Teaching experience (now JSONB array)
  experience: [],
  setExperience: (experience) => set({ experience: experience }),
  
  // Step 4: Personal info
  firstName: '',
  setFirstName: (name) => set({ firstName: name }),
  lastName: '',
  setLastName: (name) => set({ lastName: name }),
  bio: '',
  setBio: (bio) => set({ bio: bio }),
  hourlyRate: '',
  setHourlyRate: (rate) => set({ hourlyRate: rate }),
  
  // CV file path
  cvFilePath: '',
  setCvFilePath: (path) => set({ cvFilePath: path }),
  
  // CV file
  cvFile: null,
  setCvFile: (file) => set({ cvFile: file }),
  
  // Navigation helpers
  nextStep: () => {
    const { currentStep } = get();
    set({ currentStep: currentStep + 1 });
  },
  prevStep: () => {
    const { currentStep } = get();
    if (currentStep > 1) {
      set({ currentStep: currentStep - 1 });
    }
  },
  isNextDisabled: () => {
    const { 
      currentStep, 
      selectedSubjects, 
      education, 
      experience,
      firstName,
      lastName,
      bio,
      hourlyRate
    } = get();
    
    if (currentStep === 1 && selectedSubjects.length === 0) return true;
    if (currentStep === 2) {
      // Check if there's at least one complete education entry
      const hasCompleteEducation = education.some(entry => 
        entry.country && 
        entry.level && 
        entry.degree && 
        entry.field && 
        entry.institution && 
        entry.year_of_completion && 
        entry.year_of_completion > 0
      );
      if (!hasCompleteEducation) return true;
    }
    if (currentStep === 3) {
      // Check if no experience is selected
      if (experience.length === 0) return true;
      
      // Check if "none" is selected (this is valid and complete)
      const hasNoPriorExperience = experience.some(exp => exp.role === "none");
      if (hasNoPriorExperience) return false;
      
      // For all other experiences, check mandatory fields
      const hasIncompleteExperience = experience.some(exp => 
        exp.role !== "none" && (
          !exp.start_year || 
          !exp.end_year || 
          !exp.organization || 
          !exp.organization.trim()
        )
      );
      if (hasIncompleteExperience) return true;
    }
    if (currentStep === 4 && (!firstName || !lastName || !bio || !hourlyRate)) return true;
    
    return false;
  },
  
  // Reset store
  resetStore: () => set({
    currentStep: 1,
    selectedSubjects: [],
    education: [],
    experience: [],
    firstName: '',
    lastName: '',
    bio: '',
    hourlyRate: '',
    cvFile: null,
    cvFilePath: '',
  }),
}));
